import http from '../utils/http';
import { notifySuccess, notifyError } from './common';
import { ccUtils as utils } from '@/common/Utils';
import { pick } from 'lodash';
import { Promise as bbPromise } from 'bluebird';

const defaultState = {
    entityName: 'space',
    baseUrl: 'api/spaces/',
    serviceAccountBaseUrl: 'api/serviceAccounts/',
    space: {},
    serviceAccount: {}, // service account for the selectedItem/space,
    readHelpUrl: '/user-guide/guide/space-detail.html',
    editHelpUrl: '/user-guide/guide/space-edit.html',
    addHelpUrl: '/user-guide/guide/space-add.html'
};

export const mutations = {
    setSpace(state, value) {
        if (value) {
            state.space = value;
        } else {
            state.space = {};
        }
    },
    setServiceAccount(state, value) {
        if (value) {
            state.serviceAccount = value;
        } else {
            state.serviceAccount = {};
        }
    }
};

export const actions = {
    fetchSpace({ commit, dispatch, state }, spaceId) {
        console.log('calling fetchSpace', spaceId);
        return http
            .get(`${state.baseUrl}${spaceId}`, {
                notifyRequestPending: true
            })
            .then(res => {
                commit('setSpace', res.data);
                return res;
            })
            .then(res => {
                // need to fetch the serviceAccount for the selected space
                if (res.data && res.data.serviceAccount) {
                    return dispatch(
                        'fetchServiceAccount',
                        res.data.serviceAccount.id
                    );
                }
            });
    },
    fetchSpaceByServiceAccount({ state }, serviceAccountId) {
        console.log('calling fetchSpaceByServiceAccount', serviceAccountId);
        return http
            .get(
                `${state.baseUrl}search?serviceAccount.id=${serviceAccountId}`,
                {
                    notifyRequestPending: false
                }
            )
            .then(res => res.data);
    },
    fetchServiceAccount({ commit, state }, serviceAccountId) {
        console.log('calling fetchSpaceServiceAccount', serviceAccountId);
        return http
            .get(`${state.serviceAccountBaseUrl}${serviceAccountId}`, {
                notifyRequestPending: true
            })
            .then(res => {
                commit('setServiceAccount', res.data);
            });
    },
    fetchChannelOptions({ state }, { spaceId }) {
        console.log('calling fetchChannelOptions', spaceId);
        return http
            .get(`${state.baseUrl}${spaceId}/channelOptions`, {
                notifyRequestPending: true
            })
            .then(res => {
                if (res && res.data && res.data.channels) {
                    return res.data.channels;
                }
            });
    },
    resetSpace({ commit }) {
        console.log('calling resetSpace');
        commit('setSpace', {});
        commit('setServiceAccount', {});
    },
    createSpaceEntity({ dispatch }, { space, tags, devices }) {
        console.log('calling createSpaceEntity');
        const successMessage = 'Space has been saved successfuly';
        const failMessage = 'Error occured while saving new space...';
        dispatch('notifier/requestPending', null, { root: true });
        return dispatch('createSpace', { space })
            .then(createdSpace => {
                return dispatch('updateTags', {
                    space: createdSpace,
                    oldTags: [],
                    newTags: tags
                });
            })
            .then(updatedSpace => {
                return dispatch('addDevices', {
                    space: updatedSpace,
                    newDevices: devices
                });
            })
            .then(() => {
                notifySuccess(dispatch, successMessage);
                setTimeout(() => {
                    dispatch(
                        'spaces/fetchAllSpaces',
                        {},
                        {
                            root: true
                        }
                    );
                }, 300);
            })
            .catch(error => {
                notifyError(dispatch, failMessage, error);
            })
            .finally(() => {
                dispatch('notifier/reset', null, { root: true });
            });
    },
    updateSpaceEntity(
        { dispatch },
        { oldSpace, newSpace, oldChannelMapId, newChannelMapId }
    ) {
        console.log('calling updateSpaceEntity');
        const successMessage = 'Space has been saved successfuly';
        const failMessage = 'Error occured while saving this space...';
        dispatch('notifier/requestPending', null, { root: true });
        return dispatch('updateSpace', { oldSpace, newSpace })
            .then(updatedSpace => {
                return dispatch('updateTags', {
                    space: updatedSpace,
                    oldTags: oldSpace.tags,
                    newTags: newSpace.tags
                });
            })
            .then(updatedSpace => {
                return dispatch('addDevices', {
                    space: updatedSpace,
                    newDevices: newSpace.devices
                });
            })
            .then(serviceAccount => {
                return dispatch('updateChannelMap', {
                    serviceAccount,
                    oldChannelMapId,
                    newChannelMapId
                });
            })
            .then(() => {
                if (oldSpace.defaultChannel !== newSpace.defaultChannel) {
                    return dispatch('updateDefaultChannel', {
                        spaceId: newSpace.id,
                        defaultChannel: newSpace.defaultChannel
                    });
                }
            })
            .then(() => {
                notifySuccess(dispatch, successMessage);
                setTimeout(() => {
                    dispatch(
                        'spaces/fetchAllSpaces',
                        {},
                        {
                            root: true
                        }
                    );
                }, 300);
            })
            .catch(error => {
                notifyError(dispatch, failMessage, error);
            })
            .finally(() => {
                dispatch('notifier/reset', null, { root: true });
            });
    },
    createSpace({ state }, { space }) {
        // will create new space object
        console.log('calling createSpace');
        return http
            .post(state.baseUrl, utils.replaceEmptyStrings(space), {
                notifyRequestPending: false,
                notifySuccess: false,
                notifyError: false
            })
            .then(res => res.data);
    },
    updateSpace({ state }, { oldSpace, newSpace }) {
        // will update the space object attributes
        console.log('calling updateSpace');
        const spaceAttrs = [
            'id',
            'ownerId',
            'name',
            'building',
            'wing',
            'floor',
            'room',
            'visibility'
        ];
        const spaceDiff = utils.diffObject(
            pick(oldSpace, spaceAttrs),
            pick(newSpace, spaceAttrs)
        );
        if (spaceDiff && Object.keys(spaceDiff).length) {
            return http
                .patch(`${state.baseUrl}${newSpace.id}`, newSpace, {
                    notifyRequestPending: false,
                    notifySuccess: false,
                    notifyError: false
                })
                .then(res => res.data);
        }
        return oldSpace;
    },
    deleteSpace({ dispatch, state }, { space }) {
        console.log('calling deleteSpace');
        return http
            .delete(`${state.baseUrl}${space.id}`, null, {
                notifyRequestPending: true,
                notifySuccess: true,
                successMessage: 'Space, deleted successfully',
                notifyError: true
            })
            .then(() => {
                setTimeout(() => {
                    dispatch(
                        'spaces/fetchAllSpaces',
                        {},
                        {
                            root: true
                        }
                    );
                }, 300);
            });
    },
    updateTags({ dispatch }, { space, oldTags, newTags }) {
        // will add/remove tags from a space
        console.log('calling updateTags', space.id);
        const tagsDiff = utils.diffArray(
            oldTags.map(item => item.id),
            newTags.map(item => item.id)
        );
        let actions = [];
        if (space && tagsDiff && Object.keys(tagsDiff).length) {
            if (tagsDiff.add && tagsDiff.add.length) {
                actions.push({
                    action: 'addTags',
                    payload: {
                        space,
                        tagIds: tagsDiff.add
                    }
                });
            }
            if (tagsDiff.remove && tagsDiff.remove.length) {
                actions.push({
                    action: 'deleteTags',
                    payload: {
                        space,
                        tagIds: tagsDiff.remove
                    }
                });
            }
        }
        return bbPromise
            .each(actions, item => {
                return dispatch(item.action, item.payload);
            })
            .then(() => space);
    },
    addTags({ state }, { space, tagIds }) {
        // will add tags to space
        console.log('calling addTags');
        return http
            .post(`${state.baseUrl}${space.id}/tags/`, tagIds, {
                notifyRequestPending: false,
                notifySuccess: false,
                notifyError: false
            })
            .then(res => res.data);
    },
    deleteTags({ state }, { space, tagIds }) {
        // will delete tags from a space
        console.log('calling deleteTags');
        return http
            .delete(`${state.baseUrl}${space.id}/tags/`, tagIds, {
                notifyRequestPending: false,
                notifySuccess: false,
                notifyError: false
            })
            .then(res => res.data);
    },
    addDevices({ state }, { space, newDevices }) {
        // will either add/transfer devices to this space
        // will return the space service account
        console.log('calling addDevices', space.id);
        const transferredDevices = getTransferredDevices(newDevices, space);

        if (transferredDevices && transferredDevices.length) {
            const deviceIds = transferredDevices.map(item => item.id);
            return http
                .post(
                    `${state.baseUrl}${space.id}/transferDevices/`,
                    deviceIds,
                    {
                        notifyRequestPending: true,
                        notifySuccess: false,
                        notifyError: false
                    }
                )
                .then(res => {
                    return res.data.serviceAccount;
                });
        }
        return space.serviceAccount;
    },
    updateChannelMap(
        { dispatch },
        { serviceAccount, oldChannelMapId, newChannelMapId }
    ) {
        // will update the default channel map for this space
        if (serviceAccount && oldChannelMapId !== newChannelMapId) {
            return dispatch(
                'channelMaps/assignToServiceAccount',
                {
                    channelMapId: newChannelMapId,
                    serviceAccountIds: [serviceAccount.id]
                },
                { root: true }
            ).then(() => serviceAccount);
        }
        return serviceAccount;
    },
    updateDefaultChannel({ state }, { spaceId, defaultChannel }) {
        // will update the default channel for this space
        console.log('calling updateDefaultChannel');
        if (spaceId && defaultChannel) {
            return http.patch(
                `${state.baseUrl}${spaceId}/defaultChannel`,
                { defaultChannel: defaultChannel },
                {
                    notifyRequestPending: false,
                    notifySuccess: false,
                    notifyError: false
                }
            );
        }
    },
    checkIsSpaceUnique({ state }, { id, building, wing, floor, room }) {
        // will check if the room us unique or not
        // if isNewRecord there must be no rooms with that same number
        // if not isNewRecord there must be no more than 1 record with the same number
        console.log('calling checkIsSpaceUnique');
        let query = 'size=2';
        if (building) {
            query += `&building=${building}`;
        }
        if (wing) {
            query += `&wing=${wing}`;
        }
        if (floor) {
            query += `&floor=${floor}`;
        }
        if (room) {
            query += `&room=${room}`;
        }
        return http
            .get(`${state.baseUrl}search?${query}`, {
                notifyRequestPending: true,
                notifySuccess: false
            })
            .then(res => {
                return (
                    res.data.content.filter(item => {
                        return item.id !== id;
                    }).length <= 0
                );
            });
    },
    resetAccount({ dispatch }, { deviceIds }) {
        console.log('calling space resetAccount');
        const successMessage = 'Space reset completed successfully';
        const failMessage = 'Error occured during space reset action';
        if (deviceIds && deviceIds.length) {
            return dispatch(
                'device/resetByIds',
                { deviceIds: deviceIds },
                { root: true }
            )
                .then(res => {
                    if (res.length) {
                        notifySuccess(dispatch, successMessage);
                    }
                })
                .catch(error => {
                    notifyError(dispatch, failMessage, error);
                });
        }
    },
    checkInSpace({ state }, { spaceId }) {
        console.log('calling checkInSpace', spaceId);
        return http.post(`${state.baseUrl}${spaceId}/checkin`, null, {
            successMessage: 'Space checked in successfully'
        });
    },
    checkOutSpace({ state }, { spaceId }) {
        console.log('calling checkOutSpace', spaceId);
        return http.post(`${state.baseUrl}${spaceId}/checkout`, null, {
            successMessage: 'Space checked out successfully'
        });
    }
};

export default {
    namespaced: true,
    state: defaultState,
    mutations,
    actions
};

const getTransferredDevices = (newDevices, space) => {
    const spaceServiceAccountId = space.serviceAccount
        ? space.serviceAccount.id
        : 0;
    return newDevices.filter(current => {
        return (
            current.serviceAccount &&
            current.serviceAccount.id !== spaceServiceAccountId
        );
    });
};
